import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuthHeaders } from "../../utils/authUtils";
import { API_BASE_URL } from "../../environment";
import { AuthContext } from "../../context/authcontext";
import { useDispatch } from "react-redux";
import { setMobileSidebar } from "../../core/data/redux/commonSlice";
import male from "../../assets/male.png";
import { getLifecycleTypePluralName } from "../../utils/lifecycleUtils";

interface SearchResult {
  id: number;
  title: string;
  subtitle?: string;
  avatar_url?: string;
  type: string;
}

const UniversalSearch: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("Company");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);

  const [displayNames, setDisplayNames] = useState({
    Lead: "Lead",
    Pipeline: "Pipeline",
    Deal: "Deal",
    Proposals: "Proposals",
    Contracts: "Contracts",
  });

  useEffect(() => {
    if (lifecycleTypeNames && Object.keys(lifecycleTypeNames).length > 0) {
      setDisplayNames({
        Lead: lifecycleTypeNames["LEAD"] || "Lead",
        Pipeline: lifecycleTypeNames["PIPELINE"] || "Pipeline",
        Deal: lifecycleTypeNames["DEAL"] || "Deal",
        Proposals: getLifecycleTypePluralName("PROPOSAL"),
        Contracts: getLifecycleTypePluralName("CONTRACT"),
      });
    } else {
      try {
        const storedNames = JSON.parse(
          localStorage.getItem("lifecycleTypeNames") || "{}"
        );
        if (Object.keys(storedNames).length > 0) {
          setDisplayNames({
            Lead: storedNames["LEAD"] || "Lead",
            Pipeline: storedNames["PIPELINE"] || "Pipeline",
            Deal: storedNames["DEAL"] || "Deal",
            Proposals: getLifecycleTypePluralName("PROPOSAL"),
            Contracts: getLifecycleTypePluralName("CONTRACT"),
          });
        }
      } catch (error) {
        console.error(
          "Error loading lifecycle names from localStorage:",
          error
        );
      }
    }
  }, [lifecycleTypeNames]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "lifecycleTypeNames" && event.newValue) {
        try {
          const updatedNames = JSON.parse(event.newValue);
          setDisplayNames({
            Lead: updatedNames["LEAD"] || "Lead",
            Pipeline: updatedNames["PIPELINE"] || "Pipeline",
            Deal: updatedNames["DEAL"] || "Deal",
            Proposals: getLifecycleTypePluralName("PROPOSAL"),
            Contracts: getLifecycleTypePluralName("CONTRACT"),
          });
        } catch (error) {
          console.error(
            "Error parsing lifecycleTypeNames from localStorage:",
            error
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const searchCategories = [
    { value: "Company", label: "Company" },
    { value: "Contact", label: "Contact" },
    { value: "Lead", label: displayNames.Lead },
    { value: "Pipeline", label: displayNames.Pipeline },
    { value: "Deal", label: displayNames.Deal },
    { value: "Proposals", label: displayNames.Proposals },
    { value: "Contracts", label: displayNames.Contracts },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && selectedTenantId) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedCategory, selectedTenantId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performSearch = async () => {
    setIsLoading(true);
    setError("");

    try {
      const headers = getAuthHeaders();
      let url = "";

      switch (selectedCategory) {
        case "Lead":
          url = `${API_BASE_URL}/lead/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Deal":
          url = `${API_BASE_URL}/deal/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Pipeline":
          url = `${API_BASE_URL}/pipeline/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Contact":
          url = `${API_BASE_URL}/contact/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Company":
          url = `${API_BASE_URL}/company/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Proposals":
          url = `${API_BASE_URL}/proposal/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Contracts":
          url = `${API_BASE_URL}/contract/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        default:
          return;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const formattedResults: SearchResult[] =
        data.results
          ?.map((item: any) => {
            switch (selectedCategory) {
              case "Lead":
                return {
                  id: item.lead_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Lead",
                };
              case "Deal":
                return {
                  id: item.deal_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Deal",
                };
              case "Pipeline":
                return {
                  id: item.pipeline_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Pipeline",
                };
              case "Contact":
                return {
                  id: item.contact_id,
                  title: item.contact_full_name,
                  subtitle: item.company_name || item.job_title || "",
                  avatar_url: item.avatar_url || "",
                  type: "Contact",
                };
              case "Company":
                return {
                  id: item.company_id,
                  title: item.company_name,
                  subtitle:
                    item.industry_type_display_name || item.address || "",
                  avatar_url: item.company_logo || "",
                  type: "Company",
                };
              case "Proposals":
                return {
                  id: item.proposal_id,
                  title: item.subject,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Proposals",
                };
              case "Contracts":
                return {
                  id: item.contract_id,
                  title: item.subject,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Contracts",
                };
              default:
                return null;
            }
          })
          .filter(Boolean) || [];

      setSearchResults(formattedResults);
      setShowResults(formattedResults.length > 0);
    } catch (error) {
      console.error("Search error:", error);
      setError("Failed to search. Please try again.");
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    let url = "";

    switch (result.type) {
      case "Lead":
        url = `/leads/${result.id}`;
        break;
      case "Deal":
        url = `/deals/${result.id}`;
        break;
      case "Pipeline":
        url = `/pipelines/${result.id}`;
        break;
      case "Contact":
        url = `/contacts/${result.id}`;
        break;
      case "Company":
        url = `/companies/${result.id}`;
        break;
      case "Proposals":
        url = `/proposals/${result.id}`;
        break;
      case "Contracts":
        url = `/contracts/${result.id}`;
        break;
      default:
        return;
    }

    navigate(url);
    setShowResults(false);
    setSearchQuery("");
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      performSearch();
    }
  };

  const handleInputFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  return (
    <li className="nav-item nav-search-inputs me-auto">
      <div className="top-nav-search" ref={searchRef}>
        <Link to="#" className="responsive-search">
          <i className="fa fa-search" />
        </Link>
        <form onSubmit={handleSearch}>
          <div className="searchinputs d-flex align-items-center">
            <div className="search-input-wrapper position-relative">
              <input
                type="text"
                placeholder={`Search ${selectedCategory === "Lead"
                    ? displayNames.Lead
                    : selectedCategory === "Deal"
                      ? displayNames.Deal
                      : selectedCategory === "Pipeline"
                        ? displayNames.Pipeline
                        : selectedCategory
                  } Name...`}
                value={searchQuery ?? ""}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleInputFocus}
                className="search-input pe-5"
                style={{
                  paddingRight: "120px",
                }}
              />

              {/* Loading indicator */}
              {isLoading && (
                <div
                  className="position-absolute"
                  style={{
                    right: "125px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              <div
                className="search-category-dropdown position-absolute"
                style={{
                  right: "5px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                }}
              >
                <select
                  value={selectedCategory ?? ""}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="form-select search-category-select"
                  style={{
                    width: "120px",
                    height: "38px",
                    fontSize: "13px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                  }}
                >
                  {searchCategories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search Results Dropdown */}
            {showResults && (
              <div
                className="search-results-dropdown position-absolute bg-white border rounded shadow-lg"
                style={{
                  top: "100%",
                  left: "0",
                  right: "0",
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                  marginTop: "5px",
                }}
              >
                {error && (
                  <div className="p-3 text-danger small">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                {searchResults.length > 0 && (
                  <>
                    <div className="px-3 py-2 bg-light border-bottom">
                      <small className="text-muted">
                        {searchResults.length} result
                        {searchResults.length > 1 ? "s" : ""} found
                      </small>
                    </div>
                    {searchResults.map((result) => (
                      <div
                        key={`${result.type}-${result.id}`}
                        className="search-result-item p-3 border-bottom cursor-pointer"
                        onClick={() => handleResultClick(result)}
                        style={{
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <div className="d-flex align-items-center">
                          {result.type === "Contact" ? (
                            <img
                              src={result.avatar_url || male}
                              alt={result.title}
                              className="rounded-circle me-3"
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                              }}
                            />
                          ) : result.avatar_url && result.type === "Company" ? (
                            <img
                              src={result.avatar_url}
                              alt={result.title}
                              className="rounded-circle me-3"
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              className="rounded-circle me-3 d-flex align-items-center justify-content-center text-primary"
                              style={{
                                width: "40px",
                                height: "40px",
                                backgroundColor: "#e9ecef",
                              }}
                            >
                              <i
                                className={
                                  result.type === "Company"
                                    ? "ti ti-building-community"
                                    : result.type === "Contact"
                                      ? "ti ti-user-up"
                                      : result.type === "Lead"
                                        ? "ti ti-chart-arcs"
                                        : result.type === "Deal"
                                          ? "ti ti-medal"
                                          : result.type === "Pipeline"
                                            ? "ti ti-timeline-event-exclamation"
                                            : result.type === "Proposals"
                                              ? "ti ti-file-text"
                                              : result.type === "Contracts"
                                                ? "ti ti-file-certificate"
                                                : "ti ti-sitemap"
                                }
                              ></i>
                            </div>
                          )}
                          <div className="flex-grow-1">
                            <div className="fw-medium">{result.title}</div>
                            {result.subtitle && (
                              <small className="text-muted">
                                {result.subtitle}
                              </small>
                            )}
                            <div>
                              <span className="badge bg-primary bg-opacity-10 text-primary small">
                                {result.type === "Lead"
                                  ? displayNames.Lead
                                  : result.type === "Deal"
                                    ? displayNames.Deal
                                    : result.type === "Pipeline"
                                      ? displayNames.Pipeline
                                      : result.type}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {searchResults.length === 0 &&
                  !isLoading &&
                  !error &&
                  searchQuery && (
                    <div className="p-3 text-muted text-center">
                      <i className="fa fa-search me-2"></i>
                      No record found
                    </div>
                  )}
              </div>
            )}
          </div>
        </form>
      </div>
    </li>
  );
};

// Mobile version of UniversalSearch
export const MobileUniversalSearch: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("Company");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);

  const [displayNames, setDisplayNames] = useState({
    Lead: "Lead",
    Pipeline: "Pipeline",
    Deal: "Deal",
    Proposals: "Proposals",
    Contracts: "Contracts",
  });

  useEffect(() => {
    if (lifecycleTypeNames && Object.keys(lifecycleTypeNames).length > 0) {
      setDisplayNames({
        Lead: lifecycleTypeNames["LEAD"] || "Lead",
        Pipeline: lifecycleTypeNames["PIPELINE"] || "Pipeline",
        Deal: lifecycleTypeNames["DEAL"] || "Deal",
        Proposals: getLifecycleTypePluralName("PROPOSAL"),
        Contracts: getLifecycleTypePluralName("CONTRACT"),
      });
    } else {
      try {
        const storedNames = JSON.parse(
          localStorage.getItem("lifecycleTypeNames") || "{}"
        );
        if (Object.keys(storedNames).length > 0) {
          setDisplayNames({
            Lead: storedNames["LEAD"] || "Lead",
            Pipeline: storedNames["PIPELINE"] || "Pipeline",
            Deal: storedNames["DEAL"] || "Deal",
            Proposals: getLifecycleTypePluralName("PROPOSAL"),
            Contracts: getLifecycleTypePluralName("CONTRACT"),
          });
        }
      } catch (error) {
        console.error(
          "Error loading lifecycle names from localStorage:",
          error
        );
      }
    }
  }, [lifecycleTypeNames]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "lifecycleTypeNames" && event.newValue) {
        try {
          const updatedNames = JSON.parse(event.newValue);
          setDisplayNames({
            Lead: updatedNames["LEAD"] || "Lead",
            Pipeline: updatedNames["PIPELINE"] || "Pipeline",
            Deal: updatedNames["DEAL"] || "Deal",
            Proposals: getLifecycleTypePluralName("PROPOSAL"),
            Contracts: getLifecycleTypePluralName("CONTRACT"),
          });
        } catch (error) {
          console.error(
            "Error parsing lifecycleTypeNames from localStorage:",
            error
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const searchCategories = [
    { value: "Company", label: "Company" },
    { value: "Contact", label: "Contact" },
    { value: "Lead", label: displayNames.Lead },
    { value: "Pipeline", label: displayNames.Pipeline },
    { value: "Deal", label: displayNames.Deal },
    { value: "Proposals", label: displayNames.Proposals },
    { value: "Contracts", label: displayNames.Contracts },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && selectedTenantId) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedCategory, selectedTenantId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performSearch = async () => {
    setIsLoading(true);
    setError("");

    try {
      const headers = getAuthHeaders();
      let url = "";

      switch (selectedCategory) {
        case "Lead":
          url = `${API_BASE_URL}/lead/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Deal":
          url = `${API_BASE_URL}/deal/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Pipeline":
          url = `${API_BASE_URL}/pipeline/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Contact":
          url = `${API_BASE_URL}/contact/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Company":
          url = `${API_BASE_URL}/company/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Proposals":
          url = `${API_BASE_URL}/proposal/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Contracts":
          url = `${API_BASE_URL}/contract/?tenant_id=${selectedTenantId}&is_archive=false&search=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        default:
          return;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const formattedResults: SearchResult[] =
        data.results
          ?.map((item: any) => {
            switch (selectedCategory) {
              case "Lead":
                return {
                  id: item.lead_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Lead",
                };
              case "Deal":
                return {
                  id: item.deal_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Deal",
                };
              case "Pipeline":
                return {
                  id: item.pipeline_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Pipeline",
                };
              case "Contact":
                return {
                  id: item.contact_id,
                  title: item.contact_full_name,
                  subtitle: item.company_name || item.job_title || "",
                  avatar_url: item.avatar_url || "",
                  type: "Contact",
                };
              case "Company":
                return {
                  id: item.company_id,
                  title: item.company_name,
                  subtitle:
                    item.industry_type_display_name || item.address || "",
                  avatar_url: item.company_logo || "",
                  type: "Company",
                };
              case "Proposals":
                return {
                  id: item.proposal_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Proposals",
                };
              case "Contracts":
                return {
                  id: item.contract_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Contracts",
                };
              default:
                return null;
            }
          })
          .filter(Boolean) || [];

      setSearchResults(formattedResults);
      setShowResults(formattedResults.length > 0);
    } catch (error) {
      console.error("Search error:", error);
      setError("Failed to search. Please try again.");
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    let url = "";

    switch (result.type) {
      case "Lead":
        url = `/leads/${result.id}`;
        break;
      case "Deal":
        url = `/deals/${result.id}`;
        break;
      case "Pipeline":
        url = `/pipelines/${result.id}`;
        break;
      case "Contact":
        url = `/contacts/${result.id}`;
        break;
      case "Company":
        url = `/companies/${result.id}`;
        break;
      case "Proposals":
        url = `/proposals/${result.id}`;
        break;
      case "Contracts":
        url = `/contracts/${result.id}`;
        break;
      default:
        return;
    }

    navigate(url);
    setShowResults(false);
    setSearchQuery("");
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      performSearch();
    }
  };

  const handleInputFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  return (
    <div className="dropdown-item mobile-search-container" ref={searchRef}>
      <form onSubmit={handleSearch}>
        <div className="search-input-wrapper position-relative">
          <input
            type="text"
            placeholder={`Search ${selectedCategory === "Lead"
                ? displayNames.Lead
                : selectedCategory === "Deal"
                  ? displayNames.Deal
                  : selectedCategory === "Pipeline"
                    ? displayNames.Pipeline
                    : selectedCategory
              } Name...`}
            value={searchQuery ?? ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleInputFocus}
            className="form-control"
            style={{
              paddingRight: "40px",
              fontSize: "14px",
              height: "38px",
            }}
          />

          {/* Loading indicator */}
          {isLoading && (
            <div
              className="position-absolute"
              style={{
                right: "45px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <div
            className="search-category-dropdown position-absolute"
            style={{
              right: "5px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
          >
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="form-select form-select-sm"
              style={{
                width: "35px",
                fontSize: "12px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                backgroundColor: "#f8f9fa",
                cursor: "pointer",
                padding: "4px 8px",
                appearance: "none",
                backgroundImage:
                  'url(\'data:image/svg+xml;charset=UTF-8,%3csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3e%3cpolyline points="6,9 12,15 18,9"%3e%3c/polyline%3e%3c/svg%3e\')',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "12px",
                textAlign: "center",
              }}
            >
              {searchCategories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search Results */}
        {showResults && (
          <div
            className="search-results-dropdown bg-white border rounded shadow-sm"
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              zIndex: 1000,
              marginTop: "5px",
            }}
          >
            {error && (
              <div className="p-2 text-danger small">
                <i className="fa fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {searchResults.length > 0 && (
              <>
                <div className="px-2 py-1 bg-light border-bottom">
                  <small className="text-muted">
                    {searchResults.length} result
                    {searchResults.length > 1 ? "s" : ""} found
                  </small>
                </div>
                {searchResults.map((result) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    className="search-result-item p-2 border-bottom cursor-pointer"
                    onClick={() => handleResultClick(result)}
                    style={{
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <div className="d-flex align-items-center">
                      {result.type === "Contact" ? (
                        <img
                          src={result.avatar_url || male}
                          alt={result.title}
                          className="rounded-circle me-2"
                          style={{
                            width: "32px",
                            height: "32px",
                            objectFit: "cover",
                          }}
                        />
                      ) : result.avatar_url && result.type === "Company" ? (
                        <img
                          src={result.avatar_url}
                          alt={result.title}
                          className="rounded-circle me-2"
                          style={{
                            width: "32px",
                            height: "32px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          className="rounded-circle me-2 d-flex align-items-center justify-content-center text-primary"
                          style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#e9ecef",
                          }}
                        >
                          <i
                            className={
                              result.type === "Company"
                                ? "ti ti-building-community"
                                : result.type === "Contact"
                                  ? "ti ti-user-up"
                                  : result.type === "Lead"
                                    ? "ti ti-chart-arcs"
                                    : result.type === "Deal"
                                      ? "ti ti-medal"
                                      : result.type === "Pipeline"
                                        ? "ti ti-timeline-event-exclamation"
                                        : result.type === "Proposals"
                                          ? "ti ti-file-text"
                                          : result.type === "Contracts"
                                            ? "ti ti-file-certificate"
                                            : "ti ti-sitemap"
                            }
                          ></i>
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <div className="fw-medium small">{result.title}</div>
                        {result.subtitle && (
                          <small className="text-muted d-block">
                            {result.subtitle}
                          </small>
                        )}
                        <div>
                          <span className="badge bg-primary bg-opacity-10 text-primary small">
                            {result.type === "Lead"
                              ? displayNames.Lead
                              : result.type === "Deal"
                                ? displayNames.Deal
                                : result.type === "Pipeline"
                                  ? displayNames.Pipeline
                                  : result.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {searchResults.length === 0 &&
              !isLoading &&
              !error &&
              searchQuery && (
                <div className="p-2 text-muted text-center">
                  <i className="fa fa-search me-2"></i>
                  No record found
                </div>
              )}
          </div>
        )}
      </form>
    </div>
  );
};

// Sidebar version of UniversalSearch
export const SidebarUniversalSearch: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("Company");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const searchRef = useRef<HTMLLIElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);

  const [displayNames, setDisplayNames] = useState({
    Lead: "Lead",
    Pipeline: "Pipeline",
    Deal: "Deal",
    Proposals: "Proposals",
    Contracts: "Contracts",
  });

  useEffect(() => {
    if (lifecycleTypeNames && Object.keys(lifecycleTypeNames).length > 0) {
      setDisplayNames({
        Lead: lifecycleTypeNames["LEAD"] || "Lead",
        Pipeline: lifecycleTypeNames["PIPELINE"] || "Pipeline",
        Deal: lifecycleTypeNames["DEAL"] || "Deal",
        Proposals: getLifecycleTypePluralName("PROPOSAL"),
        Contracts: getLifecycleTypePluralName("CONTRACT"),
      });
    } else {
      try {
        const storedNames = JSON.parse(
          localStorage.getItem("lifecycleTypeNames") || "{}"
        );
        if (Object.keys(storedNames).length > 0) {
          setDisplayNames({
            Lead: storedNames["LEAD"] || "Lead",
            Pipeline: storedNames["PIPELINE"] || "Pipeline",
            Deal: storedNames["DEAL"] || "Deal",
            Proposals: getLifecycleTypePluralName("PROPOSAL"),
            Contracts: getLifecycleTypePluralName("CONTRACT"),
          });
        }
      } catch (error) {
        console.error(
          "Error loading lifecycle names from localStorage:",
          error
        );
      }
    }
  }, [lifecycleTypeNames]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "lifecycleTypeNames" && event.newValue) {
        try {
          const updatedNames = JSON.parse(event.newValue);
          setDisplayNames({
            Lead: updatedNames["LEAD"] || "Lead",
            Pipeline: updatedNames["PIPELINE"] || "Pipeline",
            Deal: updatedNames["DEAL"] || "Deal",
            Proposals: getLifecycleTypePluralName("PROPOSAL"),
            Contracts: getLifecycleTypePluralName("CONTRACT"),
          });
        } catch (error) {
          console.error(
            "Error parsing lifecycleTypeNames from localStorage:",
            error
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const searchCategories = [
    { value: "Company", label: "Company" },
    { value: "Contact", label: "Contact" },
    { value: "Lead", label: displayNames.Lead },
    { value: "Pipeline", label: displayNames.Pipeline },
    { value: "Deal", label: displayNames.Deal },
    { value: "Proposals", label: displayNames.Proposals },
    { value: "Contracts", label: displayNames.Contracts },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && selectedTenantId) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedCategory, selectedTenantId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performSearch = async () => {
    setIsLoading(true);
    setError("");

    try {
      const headers = getAuthHeaders();
      let url = "";

      switch (selectedCategory) {
        case "Lead":
          url = `${API_BASE_URL}/lead/?tenant_id=${selectedTenantId}&is_archive=false&title=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Deal":
          url = `${API_BASE_URL}/deal/?tenant_id=${selectedTenantId}&is_archive=false&title=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Pipeline":
          url = `${API_BASE_URL}/pipeline/?tenant_id=${selectedTenantId}&is_archive=false&title=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Contact":
          url = `${API_BASE_URL}/contact/?tenant_id=${selectedTenantId}&is_archive=false&contact_full_name=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Company":
          url = `${API_BASE_URL}/company/?tenant_id=${selectedTenantId}&is_archive=false&company_name=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Proposals":
          url = `${API_BASE_URL}/proposal/?tenant_id=${selectedTenantId}&is_archive=false&title=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        case "Contracts":
          url = `${API_BASE_URL}/contract/?tenant_id=${selectedTenantId}&is_archive=false&title=${encodeURIComponent(
            searchQuery
          )}`;
          break;
        default:
          return;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const formattedResults: SearchResult[] =
        data.results
          ?.map((item: any) => {
            switch (selectedCategory) {
              case "Lead":
                return {
                  id: item.lead_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Lead",
                };
              case "Deal":
                return {
                  id: item.deal_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Deal",
                };
              case "Pipeline":
                return {
                  id: item.pipeline_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Pipeline",
                };
              case "Contact":
                return {
                  id: item.contact_id,
                  title: item.contact_full_name,
                  subtitle: item.company_name || item.job_title || "",
                  avatar_url: item.avatar_url || "",
                  type: "Contact",
                };
              case "Company":
                return {
                  id: item.company_id,
                  title: item.company_name,
                  subtitle:
                    item.industry_type_display_name || item.address || "",
                  avatar_url: item.company_logo || "",
                  type: "Company",
                };
              case "Proposals":
                return {
                  id: item.proposal_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Proposals",
                };
              case "Contracts":
                return {
                  id: item.contract_id,
                  title: item.title,
                  subtitle:
                    item.company?.company_name ||
                    item.primary_contact?.full_name ||
                    "",
                  avatar_url:
                    item.primary_contact?.avatar_url ||
                    item.company?.avatar_url ||
                    "",
                  type: "Contracts",
                };
              default:
                return null;
            }
          })
          .filter(Boolean) || [];

      setSearchResults(formattedResults);
      setShowResults(formattedResults.length > 0);
    } catch (error) {
      console.error("Search error:", error);
      setError("Failed to search. Please try again.");
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    let url = "";

    switch (result.type) {
      case "Lead":
        url = `/leads/${result.id}`;
        break;
      case "Deal":
        url = `/deals/${result.id}`;
        break;
      case "Pipeline":
        url = `/pipelines/${result.id}`;
        break;
      case "Contact":
        url = `/contacts/${result.id}`;
        break;
      case "Company":
        url = `/companies/${result.id}`;
        break;
      case "Proposals":
        url = `/proposals/${result.id}`;
        break;
      case "Contracts":
        url = `/contracts/${result.id}`;
        break;
      default:
        return;
    }

    navigate(url);
    setShowResults(false);
    setSearchQuery("");

    // Close mobile sidebar on mobile devices
    if (window.innerWidth <= 991) {
      dispatch(setMobileSidebar(false));
    }
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      performSearch();
    }
  };

  const handleInputFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  return (
    <li className="sidebar-search-item position-relative" ref={searchRef}>
      <form onSubmit={handleSearch}>
        <div className="sidebar-search-wrapper position-relative">
          <input
            type="text"
            placeholder={`Search ${selectedCategory === "Lead"
                ? displayNames.Lead
                : selectedCategory === "Deal"
                  ? displayNames.Deal
                  : selectedCategory === "Pipeline"
                    ? displayNames.Pipeline
                    : selectedCategory
              } Name...`}
            value={searchQuery ?? ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleInputFocus}
            className="sidebar-search-input"
          />

          {/* Loading indicator */}
          {isLoading && (
            <div className="sidebar-loading-indicator">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <div className="sidebar-category-dropdown">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="sidebar-category-select"
            >
              {searchCategories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </form>

      {/* Search Results */}
      {showResults && (
        <div
          className="sidebar-search-results position-absolute bg-white border rounded shadow-lg"
          style={{
            top: "100%",
            left: "0",
            right: "0",
            zIndex: 9999,
            maxHeight: "300px",
            overflowY: "auto",
            marginTop: "5px",
          }}
        >
          {error && (
            <div className="p-3 text-danger small">
              <i className="fa fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {searchResults.length > 0 && (
            <>
              <div className="px-3 py-2 bg-light border-bottom">
                <small className="text-muted">
                  {searchResults.length} result
                  {searchResults.length > 1 ? "s" : ""} found
                </small>
              </div>
              {searchResults.map((result) => (
                <div
                  key={`${result.type}-${result.id}`}
                  className="p-3 border-bottom cursor-pointer"
                  onClick={() => handleResultClick(result)}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div className="d-flex align-items-center">
                    {result.avatar_url &&
                      (result.type === "Contact" || result.type === "Company") ? (
                      <img
                        src={result.avatar_url}
                        alt={result.title}
                        className="rounded-circle me-3"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#e9ecef",
                          color: "#6c757d",
                        }}
                      >
                        <i
                          className={`fa fa-${result.type === "Company"
                              ? "building"
                              : result.type === "Contact"
                                ? "user"
                                : result.type === "Lead"
                                  ? "star"
                                  : result.type === "Deal"
                                    ? "handshake"
                                    : result.type === "Proposals"
                                      ? "file-text"
                                      : result.type === "Contracts"
                                        ? "file-contract"
                                        : "sitemap"
                            }`}
                        ></i>
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <div className="fw-medium">{result.title}</div>
                      {result.subtitle && (
                        <small className="text-muted">{result.subtitle}</small>
                      )}
                      <div>
                        <span className="badge bg-primary bg-opacity-10 text-primary small">
                          {result.type === "Lead"
                            ? displayNames.Lead
                            : result.type === "Deal"
                              ? displayNames.Deal
                              : result.type === "Pipeline"
                                ? displayNames.Pipeline
                                : result.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {searchResults.length === 0 &&
            !isLoading &&
            !error &&
            searchQuery && (
              <div className="p-3 text-muted text-center">
                <i className="fa fa-search me-2"></i>
                No record found
              </div>
            )}
        </div>
      )}
    </li>
  );
};

export default UniversalSearch;
